import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { join } from 'lit/directives/join.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  FUTURE_DELIVERIES_FETCHER,
  FutureDeliveriesFetcher,
} from './api/future-deliveries-fetcher.js'
import { FutureDelivery } from './api/future-deliveries-types.js'
import { futureDeliveriesStyle } from './future-deliveries.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { GetFutureDeliveries } from '@cloudsuite/cs-ordering-defs'


export const alertVariants = ['error', 'info', 'success', 'warning'] as const
export type alertVariantTypes = typeof alertVariants

@customElement('csc-future-deliveries')
export class CSCFutureDeliveries extends LitElement {
  static override styles: CSSResultGroup = [futureDeliveriesStyle]

  @inject(FUTURE_DELIVERIES_FETCHER)
  futureDeliveriesFetcher!: FutureDeliveriesFetcher

  @state()
  futureDeliveries: FutureDelivery[] = []

  @property({ attribute: 'party-id', type: Number })
  partyId: number | null = null

  @property({ attribute: 'shop-id', type: Number })
  shopId: number | null = null

  @property()
  endpoint: string | null = null

  @property({ attribute: 'date-locale' })
  dateLocale: string = 'en-GB'

  @property({ attribute: 'alert-variant' })
  alertVariant: alertVariantTypes[number] = 'info'

  private get futureDeliveriesRequst(): GetFutureDeliveries {
    if (this.partyId === null || this.shopId === null || this.endpoint === null) {
      throw new Error('party-id, shop-id, and endpoint are required')
    }
    return new GetFutureDeliveries(this.partyId, this.shopId)
  }

  private get futureDates(): TemplateResult[] {
    return this.futureDeliveries
      .map(delivery => delivery.deliveryDate)
      .sort((a, b) => a > b ? 1 : -1)
      .slice(0, 3)
      .map(date => date.toLocaleDateString(this.dateLocale))
      .map(dateString => html`<span class="future-date">${dateString}</span>`)
  }

  override async connectedCallback() {
    super.connectedCallback()

    this.futureDeliveries = await this.futureDeliveriesFetcher.getFutureDeliveries(this.futureDeliveriesRequst)
  }

  override render(): TemplateResult | typeof nothing {
    if (this.futureDeliveries.length === 0) { return nothing }

    return html`
      <cse-alert variant="${this.alertVariant}">
        <div slot="description">
          <strong>${t('help_text_title', { ns: 'csc-future-deliveries', default: 'Let op:' })}</strong>
          ${t('help_text_body', { ns: 'csc-future-deliveries', default: 'Er zijn al bestellingen geplaatst met een leverdatum in de toekomst:' })}
          <span class="future-dates">${join(this.futureDates, ', ')}</span>
        </div>
      </cse-alert>`
  }
}
